import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getGroupIdFromUrl,
  getLiteReturnPath,
} from '../../utils/getGroupIdFromUrl';
import { useLiff } from '../../contexts/LiffContext';
import {
  checkGroupTenant,
  getSquareSettings,
  unbindLiteGroup,
  deleteLiteShop,
  updateLiteGroupSettings,
  updateLiteSquareSettings,
  registerNewUser,
} from '../../api/contextClient';
import { getSquareBySuffix } from '../../api/liteSquaresClient';
import { getSubscriptionStatus } from '../../api/liteSubscriptionClient';
import { getLiteJwtToken, setLiteJwtToken } from '../../api/liteAuthToken';
import { isLitePaidPlan } from '../../utils/liteSubscription';
import toast from 'react-hot-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { Switch } from '@/components/ui/switch';

/**
 * SettingPage 組件
 * 用於已註冊社群修改設定
 */
export function SettingPage() {
  const navigate = useNavigate();
  const { suffix } = useParams<{ suffix?: string }>();
  const groupId = getGroupIdFromUrl();
  const { liff, isLoggedIn } = useLiff();
  const isSquareMode = Boolean(
    suffix && suffix.length >= 6 && suffix.length <= 8
  );

  const [tenantForm, setTenantForm] = useState({
    name: '',
    type: '',
    customType: '',
  });
  const [activityPublishConfig, setActivityPublishConfig] = useState<{
    enabled: boolean;
    title: string;
    content: string;
    buttonText: string;
    imageUrl: string | null;
    prefix: string;
    includeActivityTitle: boolean;
    includeActivityDescription: boolean;
    includeJoinUrl: boolean;
    suffix: string;
  }>({
    enabled: true,
    title: '活動已啟動',
    content: '{activityTitle}',
    buttonText: '立即參與',
    imageUrl: null,
    prefix: '',
    includeActivityTitle: true,
    includeActivityDescription: true,
    includeJoinUrl: true,
    suffix: '',
  });
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showUnbindConfirm, setShowUnbindConfirm] = useState(false);
  const [isUnbinding, setIsUnbinding] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteNeedCancelDialog, setShowDeleteNeedCancelDialog] =
    useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [flexExpanded, setFlexExpanded] = useState(false);
  const [replyFormatExpanded, setReplyFormatExpanded] = useState(false);
  const [typeDrawerOpen, setTypeDrawerOpen] = useState(false);
  const [userReplyFormat, setUserReplyFormat] = useState<{
    autoReplyEnabled: boolean;
    prefix: string;
    suffix: string;
    operationReply: boolean;
    /** 是否啟用「剩餘數量小於」；與輸入欄分離，未勾選時輸入欄仍顯示可輸入 */
    remainingBelowEnabled: boolean;
    /** 輸入欄 raw 值，勾與不勾皆可輸入與顯示 */
    remainingBelowInput: string;
    /** 是否在回覆／分享內容中帶入活動報名 URL（Group 與 Square 共用） */
    includeActivityUrl: boolean;
  }>({
    autoReplyEnabled: true,
    prefix: '',
    suffix: '',
    operationReply: true,
    remainingBelowEnabled: false,
    remainingBelowInput: '10',
    includeActivityUrl: true,
  });

  /**
   * 在 LIFF 環境中，確保 JWT 與當前 LINE 用戶對應：
   * - 若已登入 LIFF：呼叫 liff.getIDToken() + /api/lite/auth/register 取得新 JWT，並覆寫本地儲存。
   * - 失敗時回傳目前的 JWT（若有），由後續流程決定是否提示重新登入。
   */
  const ensureFreshJwtFromLiff = async (): Promise<string | null> => {
    // 若沒有 LIFF 或未登入，就單純回傳目前 JWT
    const current = getJwtToken();
    if (!liff || !isLoggedIn) return current;

    try {
      const lineIdToken = await liff.getIDToken();
      if (!lineIdToken) {
        return current;
      }
      const result = await registerNewUser({ lineIdToken });
      if (result.jwtToken) {
        setLiteJwtToken(result.jwtToken);
        return result.jwtToken;
      }
      return current;
    } catch (error) {
      console.warn('[SettingPage] 透過 LIFF 重新註冊 Lite 用戶失敗:', error);
      return current;
    }
  };

  // 載入租戶資訊（Group：checkGroupTenant）
  useEffect(() => {
    const loadTenantInfo = async () => {
      if (!groupId) {
        // 非 Square 時才設為 false，Square 由下方 loadSquareInfo 控制
        if (!isSquareMode) setIsLoading(false);
        return;
      }

      try {
        let lineIdToken: string | undefined;
        if (liff && isLoggedIn) {
          try {
            const token = await liff.getIDToken();
            lineIdToken = token || undefined;
          } catch (error) {
            console.warn('[SettingPage] 無法取得 LINE ID Token:', error);
          }
        }

        const result = await checkGroupTenant(groupId, lineIdToken);

        if (result.hasTenant && result.tenant) {
          const tenant = result.tenant;
          setTenantForm({
            name: tenant.name || '',
            type: tenant.type || '',
            customType: '',
          });
          const c = tenant.activityPublishConfig as
            | {
                enabled?: boolean;
                title?: string;
                content?: string;
                buttonText?: string;
                imageUrl?: string | null;
                prefix?: string;
                includeActivityTitle?: boolean;
                includeActivityDescription?: boolean;
                includeJoinUrl?: boolean;
                suffix?: string;
              }
            | undefined;
          setActivityPublishConfig({
            enabled: c?.enabled ?? true,
            title: c?.title ?? '活動已啟動',
            content: c?.content ?? '{activityTitle}',
            buttonText: c?.buttonText ?? '立即參與',
            imageUrl: c?.imageUrl ?? null,
            prefix: c?.prefix ?? '',
            includeActivityTitle: c?.includeActivityTitle ?? true,
            includeActivityDescription: c?.includeActivityDescription ?? true,
            includeJoinUrl: c?.includeJoinUrl ?? true,
            suffix: c?.suffix ?? '',
          });
          const r = tenant.userReplyFormat;
          setUserReplyFormat({
            autoReplyEnabled: r?.autoReplyEnabled ?? true,
            prefix: r?.prefix ?? '',
            suffix: r?.suffix ?? '',
            // 「用戶回報」一律啟用且不可關閉
            operationReply: true,
            remainingBelowEnabled: r?.remainingBelowThreshold != null,
            remainingBelowInput:
              typeof r?.remainingBelowThreshold === 'number'
                ? String(r.remainingBelowThreshold)
                : '10',
            // 活動報名連結一律啟用且不可關閉
            includeActivityUrl: true,
          });
        }
      } catch (error) {
        console.error('[SettingPage] 載入租戶資訊失敗:', error);
        toast.error('載入商家資訊失敗');
      } finally {
        setIsLoading(false);
      }
    };

    loadTenantInfo();
  }, [groupId, isSquareMode, liff, isLoggedIn]);

  // 載入 Square 資訊（Square 模式：名稱、類型、自訂發佈分享、自訂回覆格式）
  useEffect(() => {
    const loadSquareInfo = async () => {
      if (!isSquareMode || !suffix) return;

      setIsLoading(true);
      try {
        const jwtToken = getJwtToken();
        if (jwtToken) {
          try {
            const settings = await getSquareSettings(suffix, jwtToken);
            setTenantForm({
              name: settings.name || '',
              type: settings.type || '',
              customType: '',
            });
            const c = settings.activityPublishConfig;
            setActivityPublishConfig({
              enabled: c?.enabled ?? true,
              title: c?.title ?? '活動已啟動',
              content: c?.content ?? '{activityTitle}',
              buttonText: c?.buttonText ?? '立即參與',
              imageUrl: c?.imageUrl ?? null,
              prefix: c?.prefix ?? '',
              includeActivityTitle: c?.includeActivityTitle ?? true,
              includeActivityDescription: c?.includeActivityDescription ?? true,
              includeJoinUrl: c?.includeJoinUrl ?? true,
              suffix: c?.suffix ?? '',
            });
            const r = settings.userReplyFormat;
            setUserReplyFormat({
              autoReplyEnabled: r?.autoReplyEnabled ?? true,
              prefix: r?.prefix ?? '',
              suffix: r?.suffix ?? '',
              operationReply: r?.operationReply ?? true,
              remainingBelowEnabled: r?.remainingBelowThreshold != null,
              remainingBelowInput:
                typeof r?.remainingBelowThreshold === 'number'
                  ? String(r.remainingBelowThreshold)
                  : '10',
              includeActivityUrl: r?.includeActivityUrl ?? true,
            });
          } catch (e) {
            // 非擁有者或 API 錯誤時改為只載入公開資訊
            const square = await getSquareBySuffix(suffix);
            if (square) {
              setTenantForm({
                name: square.name || '',
                type: square.type || '',
                customType: '',
              });
            }
          }
        } else {
          const square = await getSquareBySuffix(suffix);
          if (square) {
            setTenantForm({
              name: square.name || '',
              type: square.type || '',
              customType: '',
            });
          }
        }
      } catch (error) {
        console.error('[SettingPage] 載入 Square 資訊失敗:', error);
        toast.error('載入商家資訊失敗');
      } finally {
        setIsLoading(false);
      }
    };

    loadSquareInfo();
  }, [isSquareMode, suffix]);

  // 處理返回：Square 回 /lite/shop/:suffix，Group 回 /lite?groupId=...
  const handleBack = () => {
    navigate(
      getLiteReturnPath({ suffix: isSquareMode ? suffix : undefined, groupId })
    );
  };

  // 處理儲存設定（Group 與 Square 分開 API）
  const handleSave = async () => {
    if (!tenantForm.name.trim()) {
      toast.error('請輸入商家名稱');
      return;
    }
    const jwtToken = getJwtToken();
    if (!jwtToken) {
      toast.error('請先登入');
      return;
    }

    const typeToSave =
      tenantForm.type != null && tenantForm.type.trim() !== ''
        ? tenantForm.type.trim()
        : null;

    // Square 模式：儲存商家名稱、類型與自訂發佈分享／回覆格式（與 Group 一致）
    if (isSquareMode && suffix) {
      setIsSaving(true);
      try {
        await updateLiteSquareSettings(suffix.trim().toUpperCase(), jwtToken, {
          name: tenantForm.name.trim(),
          type: typeToSave,
          activityPublishConfig: activityPublishConfig.enabled
            ? {
                enabled: true,
                title: activityPublishConfig.title.trim() || undefined,
                content: activityPublishConfig.content.trim() || undefined,
                buttonText:
                  activityPublishConfig.buttonText.trim() || undefined,
                imageUrl: activityPublishConfig.imageUrl || null,
                prefix: activityPublishConfig.prefix.trim() || undefined,
                includeActivityTitle:
                  activityPublishConfig.includeActivityTitle,
                includeActivityDescription:
                  activityPublishConfig.includeActivityDescription,
                includeJoinUrl: activityPublishConfig.includeJoinUrl,
                suffix: activityPublishConfig.suffix.trim() || undefined,
              }
            : null,
          userReplyFormat: userReplyFormat.autoReplyEnabled
            ? {
                autoReplyEnabled: true,
                prefix: userReplyFormat.prefix.trim() || undefined,
                suffix: userReplyFormat.suffix.trim() || undefined,
                operationReply: userReplyFormat.operationReply,
                remainingBelowThreshold:
                  userReplyFormat.remainingBelowEnabled &&
                  /^\d+$/.test(userReplyFormat.remainingBelowInput) &&
                  userReplyFormat.remainingBelowInput !== ''
                    ? parseInt(userReplyFormat.remainingBelowInput, 10)
                    : null,
                includeActivityUrl: userReplyFormat.includeActivityUrl,
              }
            : null,
        });
        toast.success('設定已儲存', { duration: 3000 });
      } catch (error: unknown) {
        const message =
          error instanceof Error
            ? error.message
            : typeof (error as { message?: unknown })?.message === 'string'
              ? (error as { message: string }).message
              : '儲存設定失敗，請稍後再試';
        console.error('❌ [SettingPage] 儲存 Square 設定失敗:', message, error);
        toast.error(message);
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // Group 模式：需 groupId，可儲存活動發佈設定／回覆格式
    if (!groupId) {
      toast.error('請從群組或商家進入後再儲存設定');
      return;
    }

    let lineIdToken: string | undefined;
    if (liff && isLoggedIn) {
      try {
        lineIdToken = (await liff.getIDToken()) || undefined;
      } catch {
        // 忽略
      }
    }
    if (
      !lineIdToken &&
      (import.meta.env.DEV || import.meta.env.MODE === 'development')
    ) {
      const mockUserId =
        import.meta.env.VITE_MOCK_LINE_USER_ID ||
        'U1234567890abcdefghijklmnopqrstuv';
      lineIdToken = `mock_token_${mockUserId}_${Date.now()}`;
    }

    setIsSaving(true);
    try {
      await updateLiteGroupSettings(
        groupId,
        jwtToken,
        {
          name: tenantForm.name.trim(),
          type: typeToSave,
          activityPublishConfig: activityPublishConfig.enabled
            ? {
                enabled: true,
                title: activityPublishConfig.title.trim() || undefined,
                content: activityPublishConfig.content.trim() || undefined,
                buttonText:
                  activityPublishConfig.buttonText.trim() || undefined,
                imageUrl: activityPublishConfig.imageUrl || null,
                prefix: activityPublishConfig.prefix.trim() || undefined,
                includeActivityTitle:
                  activityPublishConfig.includeActivityTitle,
                includeActivityDescription:
                  activityPublishConfig.includeActivityDescription,
                includeJoinUrl: activityPublishConfig.includeJoinUrl,
                suffix: activityPublishConfig.suffix.trim() || undefined,
              }
            : null,
          userReplyFormat: userReplyFormat.autoReplyEnabled
            ? {
                autoReplyEnabled: true,
                prefix: userReplyFormat.prefix.trim() || undefined,
                suffix: userReplyFormat.suffix.trim() || undefined,
                operationReply: userReplyFormat.operationReply,
                remainingBelowThreshold:
                  userReplyFormat.remainingBelowEnabled &&
                  /^\d+$/.test(userReplyFormat.remainingBelowInput) &&
                  userReplyFormat.remainingBelowInput !== ''
                    ? parseInt(userReplyFormat.remainingBelowInput, 10)
                    : null,
                includeActivityUrl: userReplyFormat.includeActivityUrl,
              }
            : null,
        },
        lineIdToken
      );
      toast.success('設定已儲存', { duration: 3000 });
    } catch (error) {
      console.error('❌ [SettingPage] 儲存設定失敗:', error);
      toast.error(
        error instanceof Error ? error.message : '儲存設定失敗，請稍後再試'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const getJwtToken = (): string | null => {
    if (typeof window === 'undefined') return null;

    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('jwt_token') || params.get('token');
    if (fromUrl) {
      setLiteJwtToken(fromUrl);
      return fromUrl;
    }

    return getLiteJwtToken();
  };

  /** 點擊解除綁定：直接開啟解除確認彈窗（不需先取消訂閱） */
  const handleUnbindClick = async () => {
    if (!groupId && !(isSquareMode && suffix)) {
      toast.error('請從群組或商家進入');
      return;
    }
    const jwtToken = await ensureFreshJwtFromLiff();
    if (!jwtToken) {
      toast.error('請先登入');
      return;
    }
    setShowUnbindConfirm(true);
  };

  const handleUnbind = async () => {
    if (!groupId) {
      toast.error('無法取得群組 ID');
      return;
    }
    const jwtToken = await ensureFreshJwtFromLiff();
    if (!jwtToken) {
      toast.error('請先登入');
      return;
    }
    setIsUnbinding(true);
    setShowUnbindConfirm(false);
    try {
      await unbindLiteGroup(groupId, jwtToken);
      toast.success('已解除綁定');
      navigate('/lite');
    } catch (error) {
      console.error('❌ [SettingPage] 解除綁定失敗:', error);
      toast.error(
        error instanceof Error ? error.message : '解除綁定失敗，請稍後再試'
      );
    } finally {
      setIsUnbinding(false);
    }
  };

  /** 僅在 shop 有 line_group_id 時顯示解除綁定（Group 脈絡有 groupId，Square 無） */
  const hasLineGroupId = Boolean(groupId);

  /** 點擊刪除商家：有付費訂閱且未退訂時先顯示警語並引導至訂閱方案頁退訂，否則開啟刪除確認彈窗 */
  const handleDeleteClick = async () => {
    const jwtToken = await ensureFreshJwtFromLiff();
    if (!jwtToken) {
      toast.error('請先登入');
      return;
    }
    if (!groupId && !(isSquareMode && suffix)) {
      toast.error('請從群組或商家進入');
      return;
    }
    try {
      const st = await getSubscriptionStatus(
        jwtToken,
        undefined,
        isSquareMode && suffix
          ? { squareSuffix: suffix }
          : groupId
            ? { groupId }
            : undefined
      );
      if (isLitePaidPlan(st.planSlug) && !st.cancelAtPeriodEnd) {
        setShowDeleteNeedCancelDialog(true);
        return;
      }
    } catch {
      // 取得訂閱狀態失敗，仍允許進入刪除確認
    }
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    const jwtToken = await ensureFreshJwtFromLiff();
    if (!jwtToken) {
      toast.error('請先登入');
      return;
    }
    setIsDeleting(true);
    setShowDeleteConfirm(false);
    try {
      if (isSquareMode && suffix) {
        await deleteLiteShop({ suffix }, jwtToken);
      } else if (groupId) {
        await deleteLiteShop({ groupId }, jwtToken);
      } else {
        toast.error('無法取得商家資訊');
        return;
      }
      toast.success('已刪除商家');
      navigate('/lite');
    } catch (error) {
      console.error('❌ [SettingPage] 刪除商家失敗:', error);
      toast.error(
        error instanceof Error ? error.message : '刪除商家失敗，請稍後再試'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-blue-50 to-indigo-100 px-4 py-8">
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-6 sm:p-8">
        {/* 標題區域 */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="text-gray-600 hover:text-gray-900 mb-4 flex items-center"
          >
            <span className="mr-2">←</span>
            <span>返回</span>
          </button>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">設定</h2>
        </div>

        {/* 表單 */}
        <div className="space-y-4">
          {/* 商家名稱 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              商家名稱 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={tenantForm.name}
              onChange={e =>
                setTenantForm({ ...tenantForm, name: e.target.value })
              }
              placeholder="例如：團購、烘焙、球隊、活動等"
              className="w-full p-3 text-base border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
              maxLength={255}
            />
          </div>

          {/* 商家類型（僅顯示，建立後不可修改） */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              商家類型 <span className="text-red-500">*</span>
            </label>
            <div className="w-full p-3 text-base border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-900">
              {tenantForm.type || '未設定'}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              類型僅能在建立商家時選擇，儲存後無法修改。
            </p>
          </div>

          {/* 自訂活動發佈分享文字（收合式，Toggle 控制啟用）；發佈後僅支援「分享到 LINE」純文字 */}
          <div
            className={`rounded-lg border border-gray-200 overflow-hidden bg-gray-50/50 transition-opacity ${!activityPublishConfig.enabled ? 'opacity-60' : ''}`}
          >
            <div
              role="button"
              tabIndex={0}
              onClick={() => setFlexExpanded(o => !o)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setFlexExpanded(o => !o);
                }
              }}
              className="w-full flex items-center justify-between gap-2 p-4 text-left hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <div>
                <h3 className="text-sm font-medium text-gray-700">
                  自訂活動發佈分享文字
                </h3>
              </div>
              <div
                className="flex items-center gap-2 shrink-0"
                onClick={e => e.stopPropagation()}
              >
                <Switch
                  checked={activityPublishConfig.enabled}
                  onCheckedChange={v => {
                    setActivityPublishConfig(c => ({ ...c, enabled: v }));
                    if (v)
                      toast.success('請點擊下方「儲存設定」以儲存此區設定', {
                        duration: 2500,
                      });
                  }}
                />
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className={`text-gray-500 transition-transform ${flexExpanded ? 'rotate-180' : ''}`}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </div>
            {flexExpanded && (
              <div className="border-t border-gray-100 px-4 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    前綴
                  </label>
                  <input
                    type="text"
                    value={activityPublishConfig.prefix}
                    onChange={e =>
                      setActivityPublishConfig(c => ({
                        ...c,
                        prefix: e.target.value,
                      }))
                    }
                    placeholder="例：活動已啟動，歡迎報名"
                    className="w-full p-3 text-base border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    maxLength={500}
                  />
                </div>
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-2">
                    主文
                  </span>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={activityPublishConfig.includeActivityTitle}
                        onChange={e =>
                          setActivityPublishConfig(c => ({
                            ...c,
                            includeActivityTitle: e.target.checked,
                          }))
                        }
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">活動名稱</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={
                          activityPublishConfig.includeActivityDescription
                        }
                        onChange={e =>
                          setActivityPublishConfig(c => ({
                            ...c,
                            includeActivityDescription: e.target.checked,
                          }))
                        }
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">活動說明</span>
                    </label>
                    <p className="text-xs text-gray-400 pl-6">
                      活動報名連結會自動加在訊息最後一行，無需勾選。
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    後綴
                  </label>
                  <input
                    type="text"
                    value={activityPublishConfig.suffix}
                    onChange={e =>
                      setActivityPublishConfig(c => ({
                        ...c,
                        suffix: e.target.value,
                      }))
                    }
                    placeholder="後綴文字"
                    className="w-full p-3 text-base border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    maxLength={500}
                  />
                </div>
              </div>
            )}
          </div>

          {/* 自訂用戶回覆格式（收合式，Toggle 控制啟用） */}
          <div
            className={`rounded-lg border border-gray-200 overflow-hidden bg-gray-50/50 transition-opacity ${!userReplyFormat.autoReplyEnabled ? 'opacity-60' : ''}`}
          >
            <div
              role="button"
              tabIndex={0}
              onClick={() => setReplyFormatExpanded(o => !o)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setReplyFormatExpanded(o => !o);
                }
              }}
              className="w-full flex items-center justify-between gap-2 p-4 text-left hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <h3 className="text-sm font-medium text-gray-700">
                自訂用戶回覆格式
              </h3>
              <div
                className="flex items-center gap-2 shrink-0"
                onClick={e => e.stopPropagation()}
              >
                <Switch
                  checked={userReplyFormat.autoReplyEnabled}
                  onCheckedChange={v => {
                    setUserReplyFormat(r => ({ ...r, autoReplyEnabled: v }));
                    if (v)
                      toast.success('請點擊下方「儲存設定」以儲存此區設定', {
                        duration: 2500,
                      });
                  }}
                />
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className={`text-gray-500 transition-transform ${replyFormatExpanded ? 'rotate-180' : ''}`}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </div>
            {replyFormatExpanded && (
              <div className="border-t border-gray-100 px-4 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    前綴
                  </label>
                  <input
                    type="text"
                    value={userReplyFormat.prefix}
                    onChange={e =>
                      setUserReplyFormat(r => ({
                        ...r,
                        prefix: e.target.value,
                      }))
                    }
                    placeholder="例：請點擊上方公告進入選單"
                    className="w-full p-3 text-base border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    maxLength={500}
                  />
                </div>
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-2">
                    主文
                  </span>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 cursor-default">
                      <input
                        type="checkbox"
                        checked={userReplyFormat.operationReply}
                        disabled
                        className="w-4 h-4 rounded border-gray-300 text-gray-400"
                      />
                      <span className="text-sm text-gray-500">用戶回報</span>
                    </label>
                    <div className="flex flex-nowrap items-center gap-2">
                      <label className="flex items-center gap-2 cursor-pointer shrink-0">
                        <input
                          type="checkbox"
                          checked={userReplyFormat.remainingBelowEnabled}
                          onChange={e => {
                            const checked = e.target.checked;
                            setUserReplyFormat(r => {
                              const next = {
                                ...r,
                                remainingBelowEnabled: checked,
                              };
                              if (checked && r.remainingBelowInput === '')
                                next.remainingBelowInput = '10';
                              return next;
                            });
                          }}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700 whitespace-nowrap">
                          數量小於
                        </span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={userReplyFormat.remainingBelowInput}
                        onChange={e => {
                          const value = e.target.value;
                          if (value === '' || /^\d+$/.test(value)) {
                            setUserReplyFormat(r => ({
                              ...r,
                              remainingBelowInput: value,
                            }));
                          }
                        }}
                        placeholder="數量"
                        className="w-14 p-2 text-base border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none shrink-0"
                      />
                      <span className="text-sm text-gray-500 whitespace-nowrap">
                        ，額外回覆剩於量
                      </span>
                    </div>
                    <label className="flex items-center gap-2 cursor-default">
                      <input
                        type="checkbox"
                        checked={userReplyFormat.includeActivityUrl}
                        disabled
                        className="w-4 h-4 rounded border-gray-300 text-gray-400"
                      />
                      <span className="text-sm text-gray-500">
                        活動報名連結
                      </span>
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    後綴
                  </label>
                  <input
                    type="text"
                    value={userReplyFormat.suffix}
                    onChange={e =>
                      setUserReplyFormat(r => ({
                        ...r,
                        suffix: e.target.value,
                      }))
                    }
                    placeholder="後綴文字"
                    className="w-full p-3 text-base border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    maxLength={500}
                  />
                </div>
              </div>
            )}
          </div>

          {/* 儲存按鈕 */}
          <button
            onClick={handleSave}
            disabled={isSaving || !tenantForm.name.trim()}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-all text-base min-h-[48px] touch-manipulation"
          >
            {isSaving ? '儲存中...' : '儲存設定'}
          </button>

          {/* 解除綁定按鈕：僅在 shop 的 line_group_id 有值時顯示 */}
          {hasLineGroupId && (
            <button
              type="button"
              onClick={handleUnbindClick}
              disabled={isUnbinding}
              className="w-full border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 disabled:opacity-50 disabled:cursor-not-allowed font-medium py-3 px-6 rounded-lg transition-all text-base min-h-[48px] touch-manipulation"
            >
              {isUnbinding ? '處理中...' : '解除綁定'}
            </button>
          )}

          {/* 刪除商家按鈕 */}
          <button
            type="button"
            onClick={handleDeleteClick}
            disabled={isDeleting}
            className="w-full border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed font-medium py-3 px-6 rounded-lg transition-all text-base min-h-[48px] touch-manipulation"
          >
            {isDeleting ? '處理中...' : '刪除商家'}
          </button>
        </div>

        {/* 刪除商家：有訂閱時僅顯示提醒，不自動導向訂閱方案頁 */}
        <Dialog
          open={showDeleteNeedCancelDialog}
          onOpenChange={setShowDeleteNeedCancelDialog}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>請先取消訂閱</DialogTitle>
              <DialogDescription>
                您目前有訂閱中的付費方案，請先至訂閱方案頁面取消訂閱後，再刪除商家。
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <button
                type="button"
                onClick={() => setShowDeleteNeedCancelDialog(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                我知道了
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 解除綁定確認彈窗（後果提示） */}
        <Dialog open={showUnbindConfirm} onOpenChange={setShowUnbindConfirm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>確定要解除綁定？</DialogTitle>
              <DialogDescription>
                解除後，此 LINE
                群組將不再與您的助理綁定。該群組內的活動與紀錄可能無法再使用，且需重新綁定才能恢復；此操作無法復原。
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <button
                type="button"
                onClick={() => setShowUnbindConfirm(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleUnbind}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                確定解除
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 刪除商家確認彈窗 */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>確定要刪除商家？</DialogTitle>
              <DialogDescription className="space-y-2 pt-1">
                <p>
                  刪除後此商家將停用，相關活動與紀錄可能無法再使用；此操作無法復原。
                </p>
                <p className="text-amber-600 font-medium">
                  ⚠️
                  若您有付費訂閱中的方案，請先至訂閱方案頁面取消訂閱後，再執行刪除。
                </p>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                確定刪除
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
